import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Api from "../api/Api";

// ─── Estilos injetados via <style> ───────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  /* ── Bell Button ── */
  .nc-bell-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, transform 0.15s;
    outline: none;
    color: #1677ff;
  }
  .nc-bell-btn:hover {
    background: rgba(0, 87, 217, 0.25);
    border-color: rgba(0, 140, 255, 0.4);
    transform: translateY(-1px);
  }
  .nc-bell-btn svg {
    width: 20px;
    height: 20px;
  }
  .nc-bell-btn.nc-has-pending {
    animation: nc-ring 2.5s ease-in-out infinite;
  }
  @keyframes nc-ring {
    0%, 100% { transform: rotate(0deg); }
    5%  { transform: rotate(10deg); }
    10% { transform: rotate(-10deg); }
    15% { transform: rotate(8deg); }
    20% { transform: rotate(-8deg); }
    25% { transform: rotate(0deg); }
  }

  /* ── Badge ── */
  .nc-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    background: linear-gradient(135deg, #ff4d6d, #ff1744);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    font-family: 'Inter', sans-serif;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #0d1b2e;
    box-shadow: 0 2px 8px rgba(255,23,68,0.5);
    animation: nc-badge-pop 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes nc-badge-pop {
    from { transform: scale(0); }
    to   { transform: scale(1); }
  }

  /* ── Overlay ── */
  .nc-overlay {
    position: fixed;
    inset: 0;
    background: transparent;   /* sem fundo */
    backdrop-filter: none;     /* sem blur */
    z-index: 1001;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 70px 20px 0;
    animation: nc-overlay-in 0.2s ease;
  }
  @keyframes nc-overlay-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  /* ── Panel ── */
  .nc-panel {
    width: 420px;
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 100px);
    background: linear-gradient(160deg, #0e1e3a 0%, #091428 100%);
    border: 1px solid rgba(0, 120, 255, 0.2);
    border-radius: 18px;
    box-shadow:
      0 24px 60px rgba(0,0,0,0.6),
      0 0 0 1px rgba(255,255,255,0.04) inset,
      0 1px 0 rgba(255,255,255,0.08) inset;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: nc-panel-in 0.28s cubic-bezier(0.34,1.2,0.64,1);
    font-family: 'Inter', sans-serif;
  }
  @keyframes nc-panel-in {
    from { opacity: 0; transform: translateY(-16px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── Panel Header ── */
  .nc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 22px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    flex-shrink: 0;
  }
  .nc-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .nc-header-icon {
    width: 34px;
    height: 34px;
    background: linear-gradient(135deg, #0057d9, #0095ff);
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(0,87,217,0.4);
  }
  .nc-header-icon svg { width: 16px; height: 16px; color: #fff; }
  .nc-title {
    font-size: 15px;
    font-weight: 600;
    color: #e8edf8;
    letter-spacing: -0.2px;
  }
  .nc-subtitle {
    font-size: 11px;
    color: rgba(255,255,255,0.38);
    margin-top: 1px;
  }
  .nc-close-btn {
    width: 30px;
    height: 30px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255,255,255,0.45);
    transition: background 0.2s, color 0.2s;
    outline: none;
  }
  .nc-close-btn:hover {
    background: rgba(255,255,255,0.12);
    color: #fff;
  }
  .nc-close-btn svg { width: 14px; height: 14px; }

  /* ── Tabs / Filter ── */
  .nc-tabs {
    display: flex;
    gap: 6px;
    padding: 12px 22px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-shrink: 0;
  }
  .nc-tab {
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.2s;
    color: rgba(255,255,255,0.45);
    background: transparent;
    outline: none;
  }
  .nc-tab:hover { color: rgba(255,255,255,0.7); }
  .nc-tab.active {
    background: rgba(0,87,217,0.2);
    border-color: rgba(0,140,255,0.35);
    color: #5aadff;
  }

  /* ── List ── */
  .nc-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px 12px;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.1) transparent;
  }
  .nc-list::-webkit-scrollbar { width: 4px; }
  .nc-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }

  /* ── Empty State ── */
  .nc-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 52px 24px;
    gap: 12px;
    color: rgba(255,255,255,0.3);
    text-align: center;
  }
  .nc-empty-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(255,255,255,0.04);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .nc-empty-icon svg { width: 24px; height: 24px; }
  .nc-empty-text { font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.35); }
  .nc-empty-sub  { font-size: 11px; color: rgba(255,255,255,0.2); }

  /* ── Loading ── */
  .nc-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
  }
  .nc-spinner {
    width: 28px;
    height: 28px;
    border: 2px solid rgba(255,255,255,0.08);
    border-top-color: #0078ff;
    border-radius: 50%;
    animation: nc-spin 0.7s linear infinite;
  }
  @keyframes nc-spin { to { transform: rotate(360deg); } }

  /* ── Notification Card ── */
  .nc-card {
    display: flex;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    margin-bottom: 6px;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background 0.18s, border-color 0.18s, transform 0.15s;
    position: relative;
    overflow: hidden;
    text-align: left;
    width: 100%;
    font-family: 'Inter', sans-serif;
  }
  .nc-card::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 0.2s;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(0,120,255,0.06), transparent);
  }
  .nc-card:hover::before { opacity: 1; }
  .nc-card:hover {
    transform: translateX(3px);
    border-color: rgba(255,255,255,0.08);
  }
  .nc-card:active { transform: translateX(3px) scale(0.99); }

  /* Pending card */
  .nc-card.pending {
    background: rgba(0, 87, 217, 0.1);
    border-color: rgba(0, 120, 255, 0.2);
  }
  .nc-card.pending:hover {
    background: rgba(0, 87, 217, 0.16);
    border-color: rgba(0, 150, 255, 0.35);
  }
  /* Read card */
  .nc-card.read {
    background: rgba(255,255,255,0.03);
    border-color: rgba(255,255,255,0.05);
  }
  .nc-card.read:hover {
    background: rgba(255,255,255,0.055);
  }
  /* Clicking state */
  .nc-card.nc-clicking {
    opacity: 0.65;
    pointer-events: none;
  }

  /* ── Card Icon ── */
  .nc-card-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .nc-card-icon svg { width: 17px; height: 17px; }
  .pending .nc-card-icon {
    background: linear-gradient(135deg, rgba(0,87,217,0.35), rgba(0,140,255,0.2));
    color: #5aadff;
  }
  .read .nc-card-icon {
    background: rgba(255,255,255,0.06);
    color: rgba(255,255,255,0.3);
  }

  /* ── Card Body ── */
  .nc-card-body { flex: 1; min-width: 0; }
  .nc-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 3px;
  }
  .nc-card-title {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .pending .nc-card-title { color: #c8deff; }
  .read    .nc-card-title { color: rgba(255,255,255,0.45); }

  .nc-card-desc {
    font-size: 12px;
    line-height: 1.45;
    margin-bottom: 6px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .pending .nc-card-desc { color: rgba(200,222,255,0.6); }
  .read    .nc-card-desc { color: rgba(255,255,255,0.28); }

  .nc-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .nc-card-time {
    font-size: 10px;
    color: rgba(255,255,255,0.25);
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .nc-card-time svg { width: 10px; height: 10px; }

  /* Status pill */
  .nc-pill {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 20px;
    flex-shrink: 0;
  }
  .nc-pill.pending {
    background: rgba(0,120,255,0.18);
    color: #5aadff;
    border: 1px solid rgba(0,140,255,0.3);
  }
  .nc-pill.read {
    background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.25);
    border: 1px solid rgba(255,255,255,0.08);
  }

  /* Unread dot */
  .nc-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #0095ff;
    flex-shrink: 0;
    margin-top: 4px;
    box-shadow: 0 0 6px rgba(0,149,255,0.6);
  }

  /* ── Footer ── */
  .nc-footer {
    padding: 10px 22px 14px;
    border-top: 1px solid rgba(255,255,255,0.06);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }
  .nc-refresh-info {
    font-size: 10px;
    color: rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .nc-refresh-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    animation: nc-pulse 2s ease-in-out infinite;
  }
  @keyframes nc-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.5; transform: scale(0.85); }
  }
  .nc-mark-all-btn {
    font-size: 11px;
    font-weight: 500;
    color: #5aadff;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: background 0.2s;
    font-family: 'Inter', sans-serif;
  }
  .nc-mark-all-btn:hover { background: rgba(0,120,255,0.12); }

  /* Responsive */
  @media (max-width: 480px) {
    .nc-overlay { padding: 65px 8px 0; }
    .nc-panel { width: 100%; border-radius: 14px; }
  }
`;

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    <line x1="4" y1="4" x2="20" y2="20" strokeWidth="1.5"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(str) {
  if (!str) return "";
  const d = new Date(str.replace(" ", "T"));
  if (isNaN(d)) return str;
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ─── Main Component ───────────────────────────────────────────────────────────
const NotificationCenter = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "pending" | "read"
  const [clicking, setClicking] = useState(null);
  const intervalRef = useRef(null);
  const userStored = JSON.parse(localStorage.getItem("user"));

  const loadUpdates = useCallback(async () => {
    try {
      const data = await Api.ListUpdate({id_user: userStored.id});
      if (Array.isArray(data)) setUpdates(data);
    } catch (err) {
      console.error("Erro ao carregar atualizações:", err);
    }
  }, []);

  // Initial load + polling
  useEffect(() => {
    setLoading(true);
    loadUpdates().finally(() => setLoading(false));

   // intervalRef.current = setInterval(loadUpdates, 3000000000000000000000000000000000000000000000000000000000000000000000000000000000000);
  //  return () => clearInterval(intervalRef.current);
  }, [loadUpdates]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  const pendingCount = updates.filter((u) => u.status === "PENDENTE").length;

  const filtered = updates.filter((u) => {
    if (filter === "pending") return u.status === "PENDENTE";
    if (filter === "read")    return u.status === "LIDO";
    return true;
  });

  const handleClick = async (update) => {
    if (clicking === update.id) return;
    setClicking(update.id);
    try {
      if (update.status === "PENDENTE") {
        await Api.AlterUpdate({id:update.id});
        setUpdates((prev) =>
          prev.map((u) => u.id === update.id ? { ...u, status: "LIDO" } : u)
        );
      }
      setOpen(false);
      navigate(update.rota);
    } catch (err) {
      console.error("Erro ao atualizar notificação:", err);
    } finally {
      setClicking(null);
    }
  };

  const handleMarkAllRead = async () => {
    const pending = updates.filter((u) => u.status === "PENDENTE");
    await Promise.allSettled(pending.map((u) => Api.AlterUpdate({id:u.id})));
    setUpdates((prev) => prev.map((u) => ({ ...u, status: "LIDO" })));
  };

  return (
    <>
      <style>{styles}</style>

      {/* ── Bell Button ── */}
      <button
        className={`nc-bell-btn${pendingCount > 0 ? " nc-has-pending" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title="Atualizações"
        aria-label={`Notificações${pendingCount > 0 ? ` — ${pendingCount} pendentes` : ""}`}
      >
        <BellIcon />
        {pendingCount > 0 && (
          <span className="nc-badge" key={pendingCount}>
            {pendingCount > 99 ? "99+" : pendingCount}
          </span>
        )}
      </button>

      {/* ── Overlay + Panel ── */}
      {open && (
        <div className="nc-overlay" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="nc-panel" role="dialog" aria-label="Central de Notificações">

            {/* Header */}
            <div className="nc-header">
              <div className="nc-header-left">
                <div className="nc-header-icon"><BellIcon /></div>
                <div>
                  <div className="nc-title">Atualizações</div>
                  <div className="nc-subtitle">
                    {pendingCount > 0
                      ? `${pendingCount} pendente${pendingCount > 1 ? "s" : ""}`
                      : ""}
                  </div>
                </div>
              </div>
              <button className="nc-close-btn" onClick={() => setOpen(false)} aria-label="Fechar">
                <CloseIcon />
              </button>
            </div>

            {/* Tabs */}
            <div className="nc-tabs">
              {[
                { key: "all",     label: `Todas (${updates.length})` },
                { key: "pending", label: `Pendentes (${pendingCount})` },
                { key: "read",    label: `Lidas (${updates.length - pendingCount})` },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`nc-tab${filter === key ? " active" : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* List */}
            <div className="nc-list">
              {loading ? (
                <div className="nc-loading"><div className="nc-spinner" /></div>
              ) : filtered.length === 0 ? (
                <div className="nc-empty">
                  <div className="nc-empty-icon"><EmptyIcon /></div>
                  <div className="nc-empty-text">
                    {filter === "pending" ? "Nenhuma notificação pendente" : "Nenhuma notificação encontrada"}
                  </div>
                  <div className="nc-empty-sub">Você está em dia!</div>
                </div>
              ) : (
                filtered.map((u) => {
                  const isPending = u.status === "PENDENTE";
                  return (
                    <button
                      key={u.id}
                      className={`nc-card ${isPending ? "pending" : "read"}${clicking === u.id ? " nc-clicking" : ""}`}
                      onClick={() => handleClick(u)}
                      title={u.rota}
                    >
                      {/* Icon */}
                      <div className="nc-card-icon">
                        {isPending ? <AlertIcon /> : <CheckIcon />}
                      </div>

                      {/* Body */}
                      <div className="nc-card-body">
                        <div className="nc-card-top">
                          <div className="nc-card-title">{u.titulo}</div>
                          <span className={`nc-pill ${isPending ? "pending" : "read"}`}>
                            {isPending ? "Pendente" : "Lido"}
                          </span>
                        </div>
                        <div className="nc-card-desc">{u.descricao}</div>
                        <div className="nc-card-footer">
                          <span className="nc-card-time">
                            <ClockIcon /> {formatDate(u.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Unread dot */}
                      {isPending && <div className="nc-dot" />}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="nc-footer">
              <div className="nc-refresh-info">
                <div className="nc-refresh-dot" />
                Atualiza a cada 30s
              </div>
          
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationCenter;